import { Injectable } from '@angular/core';
import { EntityActions } from '@datorama/akita';
import { of, Observable } from 'rxjs';
import { CrowdCountObserved } from '../../model/crowd-count-observed';

import { CrowdFlowROI } from '../../model/crowd-flow-roi';
import { CrowdCountObservedService } from '../service/crowd-count-observed.service';
import { CrowdFlowROIService } from '../service/crowd-flow-roi.service';
import { CrowdFlowSensorSiteService } from '../service/crowd-flow-sensor-site.service';
import { CrowdFlowsObservedService } from '../service/crowd-flows-observed.service';
import { CrowdCountObservedQuery } from '../state/crowd-count-observed.query';
import { CrowdFlowROIQuery } from '../state/crowd-flow-roi.query';
import { CrowdFlowSensorSiteQuery } from '../state/crowd-flow-sensor-site.query';
import { CrowdFlowsObservedQuery } from '../state/crowd-flows-observed.query';
import { CrowdFlowAlertsService } from '../service/crowd-flow-alerts.service';
import { UrlConfig } from '../../model/urlconfig';

@Injectable({
  providedIn: 'root'
})
export class CrowdFlowUtilsService implements UrlConfig {
  constructor(
    private crowdCountObservedQuery: CrowdCountObservedQuery,
    private crowdCountObservedService: CrowdCountObservedService,
    private crowdFlowObservedService: CrowdFlowsObservedService,
    private crowdFlowROIQuery: CrowdFlowROIQuery,
    private crowdFlowROIService: CrowdFlowROIService,
    private crowdFlowSensorSiteQuery: CrowdFlowSensorSiteQuery,
    private crowdFlowSensorSiteService: CrowdFlowSensorSiteService,
    private crowdFlowsObservedQuery: CrowdFlowsObservedQuery,
    private crowdFlowAlertsService: CrowdFlowAlertsService
  ) { }

  loadAllCrowdFlowData() {
    this.crowdFlowObservedService.getAll();
    this.crowdFlowSensorSiteService.getAll();
    this.crowdFlowROIService.getAll();
    this.crowdCountObservedService.getAll();
    this.crowdFlowAlertsService.getAll();
  }

  selectAllCrowdFlows() {
    return this.crowdFlowsObservedQuery.selectAll();
  }

  updateEntitiesUrl(newUrl: string): void {
    this.crowdFlowROIService.updateEntitiesUrl(newUrl);
    this.crowdFlowObservedService.updateEntitiesUrl(newUrl);
    this.crowdCountObservedService.updateEntitiesUrl(newUrl);
    this.crowdFlowSensorSiteService.updateEntitiesUrl(newUrl);
    this.crowdFlowAlertsService.updateEntitiesUrl(newUrl);
  }

  selectCrowdFlowById(entityId: string | number) {
    if (entityId == null) {
      return of(null);
    }
    return this.crowdFlowsObservedQuery.selectEntity(entityId);
  }

  selectROIByEdgeIds(edgeId: string | number) {
    return this.crowdFlowROIQuery.roisByEdge$(edgeId);
  }

  getAllCrowdFlowsAsObjects() {
    return this.crowdFlowROIQuery.getAll();
  }

  selectROIsOnDelete() {
    return this.crowdFlowROIQuery.selectEntityAction(EntityActions.Remove);
  }

  selectROI(entityid: string | number) {
    return this.crowdFlowROIQuery.selectEntity(entityid);
  }

  /**
   * Returns the ROI with crowdCount if the roiID exists, otherwise returns NULL observable
   */
  selectROIWithCrowdCount(roiID: string | number): Observable<CrowdFlowROI> {
    if (!this.crowdFlowROIQuery.hasEntity(roiID)) {
      return of(null);
    } else {
      return this.crowdFlowROIQuery.selectROIWithCrowdCountObserved(roiID);
    }
  }

  selectCrowdCountById(crowdCountId: string | number): Observable<CrowdCountObserved> {
    if (crowdCountId == null) {
      return of(null);
    }
    return this.crowdCountObservedQuery.selectEntity(crowdCountId);

  }

  getROIByID(id: string | number) {
    return this.crowdFlowROIQuery.getEntity(id);
  }

  selectAllCrowdFlowSensorSites() {
    return this.crowdFlowSensorSiteQuery.selectAll();
  }

  selectallROIs() {
    return this.crowdFlowROIQuery.selectAll();
  }

  selectAllCrowdFlowsForRoiID(roidID: string) {
    return this.crowdFlowROIQuery.selectAllCrowdFlowsForSingleRoi(roidID);
  }

  selectAllSensorSitesWithoutROI() {
    return this.crowdFlowROIQuery.selectAllSensorSitesWithoutROIs();
  }

  insertROI(roi: CrowdFlowROI) {
    this.crowdFlowROIService.addToHub(roi);
  }

  deleteRoi(roi: CrowdFlowROI) {
    this.crowdFlowROIService.deleteRoi(roi);
  }

  nameRoi(id: string | number, name: string) {
    const roi = this.getROIByID(id);
    if (roi) {
      this.crowdFlowROIService.nameRoi(roi, name);
    }
  }

  nameEdge(edgeId: string | number, roi: CrowdFlowROI, name: string) {
    this.crowdFlowROIService.nameEdge(edgeId, roi, name);
  }
}
